/**
 * Global date range filter — chip group of presets + custom popover.
 *
 * Lives in the layout header so every page sees the same control.
 * Pages opt-in to honoring the value via `useDateRange()`.
 */
import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import {
  DateRangePreset,
  PRESET_LABELS,
  formatRangeShort,
  useDateRange,
} from '@/lib/dateRange';

const QUICK_PRESETS: DateRangePreset[] = [
  'today',
  'yesterday',
  'l7d',
  'l14d',
  'l28d',
];

const MORE_PRESETS: DateRangePreset[] = ['l90d', 'l365d', 'mtd', 'ytd'];

function toInputDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromInputDate(s: string, endOfDay: boolean): Date | null {
  if (!s) return null;
  const parts = s.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(
    Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0),
  );
}

export function DateRangeFilter() {
  const { range, setPreset, setCustom } = useDateRange();
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(toInputDate(range.start));
  const [customEnd, setCustomEnd] = useState(toInputDate(range.end));
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync inputs whenever the global range changes (e.g. preset click).
  useEffect(() => {
    setCustomStart(toInputDate(range.start));
    setCustomEnd(toInputDate(range.end));
  }, [range.start, range.end]);

  // Close popover on outside click or Esc.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  function applyCustom() {
    const s = fromInputDate(customStart, false);
    const e = fromInputDate(customEnd, true);
    if (!s || !e) return;
    if (e <= s) return;
    setCustom(s, e);
    setOpen(false);
  }

  return (
    <div className="relative" ref={popoverRef}>
      <div className="flex items-center gap-1">
        {/* Quick chips */}
        <div className="hidden gap-1 md:flex">
          {QUICK_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                range.preset === p
                  ? 'bg-blue-600/30 text-blue-200 ring-1 ring-blue-500/50'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
              title={PRESET_LABELS[p]}
            >
              {chipLabel(p)}
            </button>
          ))}
        </div>

        {/* Trigger for the full popover with all presets + custom dates */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition ${
            range.preset === 'custom' || !QUICK_PRESETS.includes(range.preset)
              ? 'border-blue-500/40 bg-blue-600/20 text-blue-200'
              : 'border-slate-700 text-slate-300 hover:bg-slate-800'
          }`}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <Calendar className="h-3.5 w-3.5" />
          <span className="max-w-[12rem] truncate">
            {formatRangeShort(range)}
          </span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-slate-700 bg-slate-900 p-4 shadow-2xl"
        >
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Período
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            {[...QUICK_PRESETS, ...MORE_PRESETS].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setPreset(p);
                  setOpen(false);
                }}
                className={`rounded-md px-2.5 py-1.5 text-left text-xs transition ${
                  range.preset === p
                    ? 'bg-blue-600/30 text-blue-200 ring-1 ring-blue-500/50'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>

          <div className="mt-4 border-t border-slate-800 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Personalizado
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-xs">
                <span className="w-12 text-slate-400">De:</span>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                <span className="w-12 text-slate-400">Até:</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
                />
              </label>
              <button
                type="button"
                onClick={applyCustom}
                className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
              >
                Aplicar período
              </button>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            UTC. Janela atual: {range.start.toISOString().slice(0, 16)}Z →{' '}
            {range.end.toISOString().slice(0, 16)}Z
          </div>
        </div>
      )}
    </div>
  );
}

function chipLabel(p: DateRangePreset): string {
  switch (p) {
    case 'today':
      return 'Hoje';
    case 'yesterday':
      return 'Ontem';
    case 'l7d':
      return '7d';
    case 'l14d':
      return '14d';
    case 'l28d':
      return '28d';
    case 'l90d':
      return '90d';
    case 'l365d':
      return '365d';
    case 'mtd':
      return 'MTD';
    case 'ytd':
      return 'YTD';
    case 'custom':
      return 'Custom';
  }
}
